'use client';

import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { MessageCircle, Flag, Trash2 } from 'lucide-react';
import { useAuth } from '../AuthContext';
import { trackEvent } from '../../lib/analytics';
import { safeAvatarUrl } from '../../lib/safeAvatarUrl';
import {
  createComment, deleteComment, createReport, getCommentsForPost,
  type BlogCommentDocument, type BlogPostDocument,
} from '../../firebase/blog-db';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';

interface CommentSectionProps {
  post: BlogPostDocument;
  onRequireAuth: () => void;
}

function timeAgo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

export function CommentSection({ post, onRequireAuth }: CommentSectionProps) {
  const { user, isAuthenticated } = useAuth();
  const [comments, setComments] = useState<BlogCommentDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [draft, setDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [reportTarget, setReportTarget] = useState<BlogCommentDocument | null>(null);
  const [reportReason, setReportReason] = useState('');

  const load = async () => {
    setLoading(true);
    setError(false);
    try {
      const list = await getCommentsForPost(post.id!);
      setComments(list);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [post.id]);

  const submit = async () => {
    if (!isAuthenticated || !user) {
      onRequireAuth();
      return;
    }
    const body = draft.trim();
    if (!body) return;
    if (body.length > 3000) {
      toast.error('Comment is too long (max 3000 characters).');
      return;
    }
    setSubmitting(true);
    try {
      await createComment({
        postId: post.id!,
        postSlug: post.slug,
        postTitle: post.title,
        authorId: user.id,
        authorName: user.name,
        authorAvatarUrl: safeAvatarUrl(user.profileImage),
        body,
      });
      setDraft('');
      toast.success('Comment posted.');
      trackEvent('comment_created', { postId: post.id! });
      await load();
    } catch {
      toast.error('Could not post your comment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId: string) => {
    if (!confirm('Delete this comment? This cannot be undone.')) return;
    try {
      await deleteComment(commentId);
      setComments(prev => prev.filter(c => c.id !== commentId));
      toast.success('Comment deleted.');
    } catch {
      toast.error('Could not delete comment.');
    }
  };

  const submitReport = async () => {
    if (!reportTarget || !user) return;
    try {
      await createReport({
        targetType: 'comment',
        targetId: reportTarget.id!,
        postId: post.id!,
        reporterId: user.id,
        reason: reportReason.trim() || 'No reason provided',
      });
      toast.success('Comment reported. Our team will review it.');
      trackEvent('comment_reported', { postId: post.id! });
    } catch {
      toast.error('Could not submit report.');
    } finally {
      setReportTarget(null);
      setReportReason('');
    }
  };

  return (
    <section aria-labelledby="comments-heading" className="mt-16">
      <h2 id="comments-heading" className="text-2xl font-bold text-gray-900 flex items-center gap-2 mb-6">
        <MessageCircle className="w-6 h-6 text-hustlance-primary" />
        Comments {comments.length > 0 && <span className="text-gray-400 font-normal">({comments.length})</span>}
      </h2>

      <div className="mb-8">
        {isAuthenticated ? (
          <div className="flex gap-3">
            <Avatar className="h-10 w-10 flex-shrink-0">
              <AvatarImage src={user?.profileImage} alt={user?.name} />
              <AvatarFallback className="bg-gradient-to-r from-hustlance-primary to-hustlance-secondary text-white">
                {user?.name?.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <Textarea
                value={draft}
                onChange={e => setDraft(e.target.value)}
                placeholder="Share your thoughts..."
                maxLength={3000}
                className="min-h-24"
                aria-label="Write a comment"
              />
              <div className="flex justify-end mt-2">
                <Button onClick={submit} disabled={submitting || !draft.trim()}>
                  {submitting ? 'Posting...' : 'Post Comment'}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 text-center">
            <p className="text-gray-600 mb-3">Sign in to join the conversation.</p>
            <Button onClick={onRequireAuth}>Sign In</Button>
          </div>
        )}
      </div>

      {loading ? (
        <p className="text-gray-400 text-sm">Loading comments...</p>
      ) : error ? (
        <p className="text-red-500 text-sm">Could not load comments. <button onClick={load} className="underline">Try again</button></p>
      ) : comments.length === 0 ? (
        <p className="text-gray-400 text-sm">No comments yet. Be the first to share your thoughts.</p>
      ) : (
        <ul className="space-y-6">
          {comments.map(c => (
            <li key={c.id} className="flex gap-3">
              <Avatar className="h-10 w-10 flex-shrink-0">
                <AvatarImage src={c.authorAvatarUrl} alt={c.authorName} />
                <AvatarFallback className="bg-gray-200 text-gray-600">
                  {c.authorName.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <div className="flex items-baseline gap-2">
                  <span className="font-semibold text-gray-900 text-sm">{c.authorName}</span>
                  <span className="text-gray-400 text-xs">{timeAgo(c.createdAt)}</span>
                </div>
                <p className="text-gray-700 mt-1 whitespace-pre-wrap break-words">{c.body}</p>
                <div className="flex items-center gap-4 mt-2">
                  {user?.id === c.authorId && (
                    <button
                      onClick={() => handleDelete(c.id!)}
                      className="text-xs text-gray-400 hover:text-red-500 transition-colors flex items-center gap-1"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  )}
                  {isAuthenticated && user?.id !== c.authorId && (
                    <button
                      onClick={() => setReportTarget(c)}
                      className="text-xs text-gray-400 hover:text-gray-600 transition-colors flex items-center gap-1"
                    >
                      <Flag className="w-3.5 h-3.5" /> Report
                    </button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={!!reportTarget} onOpenChange={open => !open && setReportTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Report comment</DialogTitle>
          </DialogHeader>
          <Textarea
            value={reportReason}
            onChange={e => setReportReason(e.target.value)}
            placeholder="Why are you reporting this comment? (optional)"
            className="min-h-20"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportTarget(null)}>Cancel</Button>
            <Button onClick={submitReport}>Submit report</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
